import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import NumberFlow from "@number-flow/react"
import { useNavigate } from "react-router"
import type {
  CommitResult,
  CommitRowError,
  CommitWarning,
} from "../../api/importsApi"
import { ENTITY_META, type EntityGroupType } from "../../api/ingestionApi"
import type { DataQualityWarning } from "../../api/types/imports"
import { AutoDismissDialog } from "../shared/AutoDismissDialog"
import { SectionLabel } from "../shared/SectionLabel"
import { DataQualityWarnings } from "./DataQualityWarnings"
import { ENTITY_ICONS, type EntityIconComponent } from "./EntityIcons"

const BTN_PRIMARY = "bg-yellow1 text-black hover:bg-yellow2"

const ENTITY_TYPES = Object.keys(ENTITY_META) as EntityGroupType[]

// A rolled-back import can fail on thousands of rows; only the first page is
// worth rendering; the rest collapse into one "and N more" line.
const MAX_ERRORS = 20

export interface CommitSummaryData {
  createdCounts: Record<EntityGroupType, number>
  updatedCounts: Record<EntityGroupType, number>
  aliasesSaved: number
  warnings: CommitWarning[]
  errors: CommitRowError[]
  /** Per-file commit results this summary was aggregated from — kept
   * alongside the totals so the dialog can tell a mixed batch (some files
   * committed, one rolled back) from a total rollback (every file failed). */
  results: CommitResult[]
}

/** True when the batch has at least one rolled-back file AND at least one
 * file that was kept (no errors) or wrote something (non-zero created/
 * updated counts) — i.e. the batch is not a *total* rollback. */
/** Every file that was attempted rolled back: nothing of this commit survived.
 *  Read per file from its OWN errors, never from the aggregate counters: an async
 *  commit omits update counts entirely, and a file that only updated existing rows
 *  legitimately reports zero created, so "all counters are zero" is not the same
 *  question and answering it instead labelled successful imports as failures. */
function isTotalRollback(results: CommitResult[]): boolean {
  return results.length > 0 && results.every((r) => r.errors.length > 0)
}

/** Every file committed without errors yet created or changed nothing, read from
 *  the raw per-file counters (junction tables included): the entity scoreboard
 *  drops junction keys, so a BOM-quantity-only change would read as empty there. */
function wroteNothing(results: CommitResult[]): boolean {
  return (
    results.length > 0 &&
    results.every(
      (r) =>
        r.errors.length === 0 &&
        [r.recordsCreated, r.recordsUpdated].every((counts) =>
          Object.values(counts ?? {}).every((n) => !n),
        ),
    )
  )
}

function isMixedBatch(results: CommitResult[]): boolean {
  const hasFailed = results.some((r) => r.errors.length > 0)
  const hasKept = results.some(
    (r) =>
      r.errors.length === 0 ||
      Object.values(r.recordsCreated).some((n) => n > 0) ||
      Object.values(r.recordsUpdated).some((n) => n > 0),
  )
  return hasFailed && hasKept
}

interface MasterDataCommitSummaryProps {
  summary: CommitSummaryData
  onDone: () => void
}

/**
 * Success (or, when `summary.errors` is non-empty, rolled-back-import) dialog
 * after a commit attempt — reuses the processing page's scoreboard to show
 * what was written, inside the shared `AutoDismissDialog` (5 s countdown,
 * draining bar, hover pauses). The dialog holds until dismissed — instead of
 * auto-closing — whenever there is anything the user must not miss: data
 * quality warnings, or (TUR-1522) row errors from a rolled-back commit.
 */
export function MasterDataCommitSummary({
  summary,
  onDone,
}: MasterDataCommitSummaryProps) {
  const { translate } = useTranslation()
  const navigate = useNavigate()

  const warnings: DataQualityWarning[] = summary.warnings.map((w) => ({
    code: w.code,
    severity: "info",
    message: w.message,
    context: w.context,
  }))
  const hasErrors = summary.errors.length > 0
  const shownErrors = summary.errors.slice(0, MAX_ERRORS)
  const hiddenErrorCount = summary.errors.length - shownErrors.length
  const blocking: DataQualityWarning[] = shownErrors.map((e) => ({
    code: e.code,
    severity: "blocking",
    message: e.message,
    params: e.params,
    sheet: e.sheet,
    row: e.row,
    column: e.column,
  }))
  const hasWarnings = hasErrors || warnings.length > 0
  const isMixed = hasErrors && isMixedBatch(summary.results)
  const rolledBack = isTotalRollback(summary.results)
  const nothingWritten = wroteNothing(summary.results)

  return (
    <AutoDismissDialog
      title={translate(
        rolledBack
          ? "MC_MD_IMPORT_FAILED"
          : nothingWritten
            ? summary.results.length > 1
              ? "MC_MD_COMMIT_NOTHING_NEW_FILES"
              : "MC_MD_COMMIT_NOTHING_NEW_FILE"
            : "MC_MD_COMMIT_SUMMARY_TITLE",
      )}
      tone={rolledBack ? "error" : nothingWritten ? "info" : "success"}
      description={
        hasErrors ? (
          <span className="text-red1">
            {translate(
              isMixed
                ? "MC_MD_IMPORT_ERR_ROLLED_BACK_PARTIAL"
                : "MC_MD_IMPORT_ERR_ROLLED_BACK",
            )}
          </span>
        ) : nothingWritten ? undefined : (
          translate("MC_MD_COMMIT_SUMMARY_SUBTITLE")
        )
      }
      hold={hasWarnings || nothingWritten}
      onDone={onDone}
      contentClassName="max-w-[min(90vw,56rem)]"
      footer={(dismiss) => (
        <>
          {/* A finished import used to end on "Done" and drop the user back on the
              import screen, with nothing saying where the rows they just committed
              went. A rolled-back batch wrote nothing, so it keeps only "Done". */}
          {!rolledBack && (
            <Button
              variant="outline"
              onClick={() => {
                dismiss()
                navigate("/material-compliance/master-data")
              }}
            >
              {translate("MC_MD_COMMIT_SUMMARY_VIEW_DATA")}
            </Button>
          )}
          <Button className={BTN_PRIMARY} onClick={dismiss}>
            {translate("MC_MD_COMMIT_SUMMARY_DONE")}
          </Button>
        </>
      )}
    >
      {!nothingWritten && (
        <div className="grid w-full grid-cols-[repeat(auto-fit,minmax(10rem,1fr))] gap-y-6">
          {ENTITY_TYPES.map((type) => (
            <CommitEntityStat
              key={type}
              icon={ENTITY_ICONS[type]}
              label={translate(ENTITY_META[type].labelKey)}
              created={summary.createdCounts[type] ?? 0}
              updated={summary.updatedCounts[type] ?? 0}
              createdLabel={translate("MC_MD_COMMIT_SUMMARY_CREATED")}
              updatedLabel={translate("MC_MD_COMMIT_SUMMARY_UPDATED")}
            />
          ))}
        </div>
      )}

      {summary.aliasesSaved > 0 && (
        <p className="text-xs text-muted-foreground">
          {translate("MC_MD_COMMIT_SUMMARY_ALIASES", {
            count: summary.aliasesSaved,
          })}
        </p>
      )}

      {hasWarnings && (
        <div className="max-h-48 w-full overflow-y-auto">
          <DataQualityWarnings
            warnings={[...blocking, ...warnings]}
            totalCount={hasErrors ? summary.errors.length : undefined}
            defaultExpanded={rolledBack}
          />
        </div>
      )}

      {hiddenErrorCount > 0 && (
        <p className="text-xs text-muted-foreground">
          {translate("MC_MD_IMPORT_ERR_MORE", { n: hiddenErrorCount })}
        </p>
      )}
    </AutoDismissDialog>
  )
}

/** One entity's icon + label, with the created count stacked above the
 * updated count (same base color, updated dimmed) so they read as related
 * but distinct. */
function CommitEntityStat({
  icon: Icon,
  label,
  created,
  updated,
  createdLabel,
  updatedLabel,
}: {
  icon: EntityIconComponent
  label: string
  created: number
  updated: number
  createdLabel: string
  updatedLabel: string
}) {
  return (
    <div className="flex flex-1 flex-col items-center gap-1.5 px-6">
      <Icon className="h-4 w-4 text-grey5" />
      <SectionLabel>{label}</SectionLabel>
      <div className="flex flex-col items-center gap-0.5">
        <div className="flex items-baseline gap-1 text-sm whitespace-nowrap text-foreground">
          <NumberFlow
            value={created}
            translate="no"
            className="notranslate font-semibold"
          />
          <span className="lowercase">{createdLabel}</span>
        </div>
        <div className="flex items-baseline gap-1 text-sm whitespace-nowrap text-foreground/60">
          <NumberFlow
            value={updated}
            translate="no"
            className="notranslate font-semibold"
          />
          <span className="lowercase">{updatedLabel}</span>
        </div>
      </div>
    </div>
  )
}
